import { useCallback } from 'react';

import { familyApi } from '@backrs_ui/grow-ui/src/redux/family/family.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { subscriptionApi } from '@backrs_ui/grow-ui/src/redux/subscription/subscriptionApi';
import { userApi } from '@backrs_ui/grow-ui/src/redux/user/user.api';

/**
 * Refreshes the three caches that change when a user gains or loses access, each its own
 * createApi instance. `UserPermissions` gates UI, so it is refetched outright and awaited:
 * the returned promise rejects if it fails, and callers must not report success on a stale
 * cache. `Subscription` and `FamilyAccount` are only invalidated — they refetch where
 * something is subscribed, and are not awaited.
 *
 * Plain redux state is out of reach here; anything read from a slice rather than a query
 * (the bootstrap-seeded user and feature flags) is only rebuilt by a reload.
 */
export const useRefreshEntitlements = () => {
  const dispatch = useAppDispatch();

  return useCallback(async () => {
    dispatch(subscriptionApi.util.invalidateTags(['Subscription']));
    dispatch(familyApi.util.invalidateTags(['FamilyAccount']));

    const refetch = dispatch(
      userApi.endpoints.fetchUserPermissions.initiate(undefined, { forceRefetch: true }),
    );

    try {
      await refetch.unwrap();
    } finally {
      // This dispatch holds its own subscription; the app's existing one keeps the entry alive.
      refetch.unsubscribe();
    }
  }, [dispatch]);
};
